/**
 * 轻量自研 i18n（U2 §2 / §5.2 / §A2 —— 不依赖 react-i18next）
 *
 *  - LocaleProvider：持有当前 locale，持久化到 localStorage('zhihui:locale')，缺省 'zh'
 *  - useT()：返回 { t, locale }，t(key, vars?) 支持 {var} 插值，缺 key 回退 zh 再回退 key 字面量
 *  - useI18n()：返回完整上下文（含 setLocale / dict）
 *  - 切换 locale 时同步注入 REST 请求头 x-locale（见 api/client.setApiLocale）
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { setApiLocale } from '../api/client';
import { zh } from './locales/zh';
import { en } from './locales/en';

export type Locale = 'zh' | 'en';

/** 词典：扁平 key -> 当前语言文案 */
export type Dict = Record<string, string>;

const DICTS: Record<Locale, Dict> = { zh, en };
const STORAGE_KEY = 'zhihui:locale';

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  /** 取文案：支持 {name} 占位插值；缺省回退 zh，再回退 key 本身 */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** 当前语言词典（供取多语言列表，如模板名） */
  dict: Dict;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readInitial(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'zh' || stored === 'en') return stored;
  } catch {
    /* localStorage 不可用时回退 zh */
  }
  return 'zh';
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitial);

  // 切换 locale：持久化 + 同步 <html lang> + 注入 REST 请求头
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
    setApiLocale(locale);
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    const dict = DICTS[locale];
    const t = (key: string, vars?: Record<string, string | number>): string => {
      let s: string = dict[key] ?? DICTS.zh[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        }
      }
      return s;
    };
    return { locale, setLocale: setLocaleState, t, dict };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** 完整 i18n 上下文（含 setLocale / dict） */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within <LocaleProvider>');
  return ctx;
}

/** 便捷 hook：返回 t 函数与当前 locale */
export function useT(): { t: I18nContextValue['t']; locale: Locale } {
  const { t, locale } = useI18n();
  return { t, locale };
}
